import { FolderTreeSelect } from '../../CommonComponents/FolderTreeSelect/FolderTreeSelect';
import { COPY_PLAN_TO_FOLDER, COPY_PLAN_TO_PUBLICATION, FOLDER_SELECT_MENU_HEIGHT } from '@opengov/stories-shared/constants';
import { RouteComponentProps } from '@reach/router';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormHelperText from '@mui/material/FormHelperText';
import FormLabel from '@mui/material/FormLabel';
import IconButton from '@mui/material/IconButton';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import CloseIcon from '@mui/icons-material/Close';
import React, { useEffect, useMemo, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';

import { IPublicationFolder, IPublicationViewMessages, IStoriesFolder, IStoryListMessages, IStoryListSelection } from '../../../../types';
import { useUpdatePublicationUpdatedAt } from '../../Publications/PublicationView/hooks/usePublicationMutations';
import { navigateToFolderList, navigateToPublicationFolderList } from '../../../state/navigation';
import {
  alphabetizeNodes,
  compilePublicationNodes,
  disableInvalidNodes,
  EDepthType,
  getDescendentTreeHeight,
  getImmediateChildrenDisplayNames,
  getParentNodeForNode,
  getPublicationDepthInfo,
  transformFolderStructureToMap
} from '../../../utils';
import { useCopyStoryFolder, useCreateStoryFolders } from '../hooks';

import { Map } from 'immutable';

import { styles } from './CopyFolderModal.styles';

export interface ICopyFolderModalProps extends RouteComponentProps {
  currentFolderId: string;
  currentFolderName: string;
  messages: IStoryListMessages;
  folderToCopy: IStoriesFolder;
  publicationFolders: IPublicationFolder[];
  folderStructure: IStoriesFolder[];
  publicationFolderStructure: IStoriesFolder[];
  rootFolderId: string;
  entityId: number;
  entitySubdomain: string;
  publicationViewMessages: IPublicationViewMessages;
  isFromPublication?: boolean;
  publicationId?: string;
  userId?: number;
  onClose(): void;
  setSection?(section: IStoryListSelection | null): void;
}

export enum ECopyLocations {
  NO_FOLDER = 'NO_FOLDER',
  IN_FOLDER = 'IN_FOLDER',
  NEW_FOLDER = 'NEW_FOLDER'
}

const locationOptions = [
  {
    value: ECopyLocations.NO_FOLDER,
    labelKey: 'noParent',
    descriptionKey: 'copyFolderToRoot',
    dataTest: 'location-option-no-folder',
    allowRootLocation: true,
    displayLocationField: false,
    displayNameField: false
  },
  {
    value: ECopyLocations.IN_FOLDER,
    labelKey: 'copyFolderInExisting',
    descriptionKey: 'copyFolderInExistingDesc',
    dataTest: 'location-option-in-folder',
    allowRootLocation: false,
    displayLocationField: true,
    locationFieldLabelKey: 'destination',
    displayNameField: false
  },
  {
    value: ECopyLocations.NEW_FOLDER,
    labelKey: 'copyFolderInNew',
    descriptionKey: 'copyFolderInNewDesc',
    dataTest: 'location-option-new-folder',
    allowRootLocation: true,
    displayLocationField: true,
    locationFieldLabelKey: 'location',
    displayNameField: true
  }
];

const publicationLocationOptions = [
  {
    value: ECopyLocations.NO_FOLDER,
    labelKey: 'noParentSection',
    descriptionKey: 'copyFolderToRootSection',
    dataTest: 'location-option-no-section',
    allowRootLocation: false,
    displayLocationField: false,
    displayNameField: false
  },
  {
    value: ECopyLocations.IN_FOLDER,
    labelKey: 'copyFolderInExistingSection',
    descriptionKey: 'copyFolderInExistingSectionDesc',
    dataTest: 'location-option-in-section',
    allowRootLocation: false,
    displayLocationField: true,
    locationFieldLabelKey: 'destination',
    displayNameField: false
  },
  {
    value: ECopyLocations.NEW_FOLDER,
    labelKey: 'copyFolderInNewSection',
    descriptionKey: 'copyFolderInNewSectionDesc',
    dataTest: 'location-option-new-section',
    allowRootLocation: false,
    displayLocationField: false,
    locationFieldLabelKey: 'location',
    displayNameField: true
  }
];

export const getFolderChildren = (
  folderMap: { [key: string]: IStoriesFolder },
  folderId?: string,
  rootFolderId?: string
): IStoriesFolder[] => {
  let siblings: IStoriesFolder[] = [];
  if (folderId === rootFolderId || !folderId) {
    siblings = Object.values(folderMap).filter((folder) => folder.depth === 1);
  } else if (folderId && folderMap[folderId]) {
    siblings = folderMap[folderId].children || [];
  }
  return siblings;
};

export const generateUniqueFolderName = (existingNames: string[], expectedName: string) => {
  let uniqueName = expectedName;
  const suchNameExist = !!existingNames.find((name) => name === uniqueName);
  if (suchNameExist) {
    const lastCopy = existingNames
      .filter((name) => name.startsWith(`${uniqueName} (`))
      .map((name) => parseInt(name.substring(uniqueName.length + 2, name.length - 1), 10) || 0)
      .sort((a, b) => b - a)[0];
    uniqueName += ` (${(lastCopy || 0) + 1})`;
  }
  return uniqueName;
};

export const CopyFolderModal: React.FC<ICopyFolderModalProps> = (props) => {
  const {
    folderToCopy,
    folderStructure,
    messages,
    entityId,
    entitySubdomain,
    onClose,
    rootFolderId,
    currentFolderName,
    isFromPublication = false,
    publicationId,
    userId,
    publicationViewMessages,
    publicationFolderStructure,
    publicationFolders,
    setSection
  } = props;

  const createStoryFoldersMutation = useCreateStoryFolders({
    title: messages.somethingWentWrongError,
    body: messages.storyFolderCreateFailed
  });
  const copyStoryFolderMutation = useCopyStoryFolder({
    title: messages.somethingWentWrongError,
    body: messages.storyFolderCopyFailed
  });

  const currentParentFolder = getParentNodeForNode(folderToCopy, folderStructure) as IStoriesFolder;
  const updatePublicationUpdatedAtMutation = useUpdatePublicationUpdatedAt();

  const [isLoading, setIsLoading] = useState(false);
  const [folderName, setFolderName] = useState('');
  const [copyPlan, setCopyPlan] = useState<string>(isFromPublication ? COPY_PLAN_TO_PUBLICATION : COPY_PLAN_TO_FOLDER);
  const [folderTitle, setFolderTitle] = useState<string>(`Copy of ${currentFolderName}`);
  const [targetFolderId, setTargetFolderId] = useState(currentParentFolder?.id || null);
  const [location, setLocation] = useState<string>(
    !currentParentFolder ? ECopyLocations.NO_FOLDER : ECopyLocations.IN_FOLDER
  );
  const [accordionOptions, setAccordionOptions] = useState([]);

  const folderMap = useMemo(() => transformFolderStructureToMap(folderStructure), [folderStructure]);
  const trimmedFolderName = useMemo(() => folderName.trim(), [folderName]);
  const trimmedFolderTitle = useMemo(() => folderTitle.trim(), [folderTitle]);
  const isPublicationCopy = copyPlan === COPY_PLAN_TO_PUBLICATION;
  const [publicationLocation, setPublicationLocation] = useState<string>(isPublicationCopy && publicationId ? publicationId : '');
  const targetFolderDepth = isPublicationCopy
    ? getPublicationDepthInfo(publicationFolderStructure, location === ECopyLocations.IN_FOLDER ? targetFolderId : publicationLocation)
    : folderMap[location === ECopyLocations.IN_FOLDER || location === ECopyLocations.NEW_FOLDER ? targetFolderId : rootFolderId]?.depth || 0;
  const currentFolderDepth = isFromPublication
    ? getPublicationDepthInfo(publicationFolderStructure, folderToCopy.id, EDepthType.MAX_CHILDREN_DEPTH)
    : getDescendentTreeHeight(folderStructure, folderToCopy.id) || 0;

  const destinationDepth = targetFolderDepth + currentFolderDepth + (location === ECopyLocations.NEW_FOLDER ? 1 : 0);
  const nestedTooDeep = destinationDepth > 6;
  const locationToChoose = isPublicationCopy ? publicationLocationOptions : locationOptions;

  const pubFolders = useMemo(() => {
    let result: IPublicationFolder[] = [];
    if (publicationFolders && publicationFolders.length) {
      result = publicationFolders.filter(
        (folder) => folder.storyFolder.title.includes('root-folder')
      );
      return alphabetizeNodes(compilePublicationNodes(result) || []);
    }
    return result;
  }, [publicationFolders]);

  const isFormValid = () => {
    if (isPublicationCopy && publicationFolders === null) return false;
    switch (location) {
      case ECopyLocations.NO_FOLDER:
        return (isPublicationCopy ? !!publicationLocation : true) && trimmedFolderTitle.length >= 3 &&
          trimmedFolderTitle.length <= 60;
      case ECopyLocations.IN_FOLDER:
        return !nestedTooDeep && targetFolderId && trimmedFolderTitle.length >= 3 &&
          trimmedFolderTitle.length <= 60;
      case ECopyLocations.NEW_FOLDER:
        return (
          !nestedTooDeep &&
          trimmedFolderName &&
          (isPublicationCopy ? publicationLocation.length >= 3 && publicationLocation.length <= 60 : true) &&
          (isPublicationCopy ? true : !!targetFolderId) &&
          trimmedFolderTitle.length >= 3 &&
          trimmedFolderTitle.length <= 60 &&
          trimmedFolderName.length >= 3 &&
          trimmedFolderName.length <= 60
        );
      default:
        return false;
    }
  };

  useEffect(() => {
    if (location === ECopyLocations.IN_FOLDER && targetFolderId === rootFolderId) {
      setTargetFolderId(null);
    }
  }, [location, targetFolderId, rootFolderId]);

  useEffect(() => {
    if (currentParentFolder) setTargetFolderId(currentParentFolder.id);
  }, [currentParentFolder]);

  useEffect(() => {
    if (copyPlan === COPY_PLAN_TO_FOLDER && (!folderStructure || !folderToCopy)) return setAccordionOptions([]);
    if (isPublicationCopy && (!publicationFolderStructure || !folderToCopy)) return setAccordionOptions([]);
    const isNodeValid = (folder: IStoriesFolder): boolean => folder.id === folderToCopy.id;
    const updatedNodeTree = disableInvalidNodes(
      folderStructure,
      isNodeValid,
      isPublicationCopy ? publicationViewMessages.cantMoveToChild : messages.cantMoveToChild
    );
    const locationOption = locationOptions.find((option) => option.value === location);
    const options = !locationOption?.allowRootLocation
      ? updatedNodeTree
      : [
        { displayName: isPublicationCopy ? publicationViewMessages.noParent : messages.noParent, id: rootFolderId },
        ...updatedNodeTree
      ];
    setAccordionOptions(options);
  }, [folderStructure, location, copyPlan]);

  const handleCopyPlanChange = (_e: React.ChangeEvent<HTMLInputElement>, value: string): void => {
    setCopyPlan(value);
    setFolderTitle('Copy of ' + currentFolderName);
    setFolderName('');
    setPublicationLocation(value === COPY_PLAN_TO_PUBLICATION && publicationId ? publicationId : '');
    setLocation(ECopyLocations.NO_FOLDER);
  };

  const onCopyFolder = async () => {
    setIsLoading(true);
    const publicationIdForFolders = isPublicationCopy ? publicationLocation : null;
    const destinationFolderId = uuidv4();
    let storyFolders = [];
    let rootFolderPubMapping = Map<string, string>();
    let parentFolderId;
    (publicationFolders ?? []).forEach((folder) => {
      if (folder.storyFolder.title.includes('root-folder') && folder.storyFolder.publication?.id) {
        rootFolderPubMapping = rootFolderPubMapping.set(folder.storyFolder.publication.id, folder.storyFolder.id);
      }
    });

    if (location === ECopyLocations.IN_FOLDER || (location === ECopyLocations.NEW_FOLDER && !isPublicationCopy)) {
      parentFolderId = targetFolderId;
    } else {
      parentFolderId = isPublicationCopy ? rootFolderPubMapping.get(publicationLocation) : rootFolderId;
    }

    const siblingsNames = isPublicationCopy ?
      getImmediateChildrenDisplayNames(
        publicationFolderStructure,
        location === ECopyLocations.IN_FOLDER ? parentFolderId : publicationLocation
      ) :
      getFolderChildren(
        folderMap,
        parentFolderId,
        isPublicationCopy ? rootFolderPubMapping.get(publicationLocation) : rootFolderId,
      ).map((f) => f.displayName);
    const title = generateUniqueFolderName(siblingsNames, location === ECopyLocations.NEW_FOLDER ? trimmedFolderName : trimmedFolderTitle);

    if (location === ECopyLocations.NEW_FOLDER) {
      const newFolderId = uuidv4();
      storyFolders = [
        {
          id: newFolderId,
          title: title,
          parentFolderId,
          entityId: entityId,
          publicationId: publicationIdForFolders
        },
        {
          title: trimmedFolderTitle,
          id: destinationFolderId,
          parentFolderId: newFolderId,
          entityId: entityId,
          publicationId: publicationIdForFolders
        }
      ];
    } else {
      storyFolders = [
        {
          title,
          id: destinationFolderId,
          parentFolderId,
          entityId: entityId,
          publicationId: publicationIdForFolders
        }
      ];
    }
    await createStoryFoldersMutation.mutateAsync({ storyFolders });
    await copyStoryFolderMutation.mutateAsync({
      sourceFolderId: folderToCopy.id,
      destinationFolderId,
      publicationId: isPublicationCopy ? publicationIdForFolders : null,
      entityId,
      entitySubdomain
    });
    setIsLoading(false);
    onClose();

    if (copyPlan === COPY_PLAN_TO_FOLDER) {
      navigateToFolderList(entitySubdomain, destinationFolderId);
    } else if (isPublicationCopy && publicationIdForFolders) {
      updatePublicationUpdatedAtMutation.mutate({ publicationId: publicationIdForFolders, userId });
      if (setSection) {
        setSection(null);
      }
      navigateToPublicationFolderList(entitySubdomain, publicationIdForFolders, destinationFolderId);
    }
  };

  const renderRadioDescription = ({
    description,
    showLocationField = false,
    locationFieldLabel,
    showNameField = false
  }: {
    description: string;
    showLocationField?: boolean;
    locationFieldLabel?: string;
    showNameField?: boolean;
  }) => {
    let existing: IStoriesFolder[] = [];
    if (isPublicationCopy) {
      if (!publicationFolderStructure) return null;
      const publicationLocationNode = publicationFolderStructure?.filter((folder) => folder.id === publicationLocation).map((folder) => folder.children);
      existing = publicationLocationNode && publicationLocationNode.length > 0 ? publicationLocationNode[0] : [];
    } else {
      existing = accordionOptions;
    }
    return (
      <Box sx={styles.radioDescription}>
        <Typography variant="body2" color="text.secondary">{description}</Typography>
        {showLocationField && (
          <>
            <Box sx={styles.spacer} />
            <Typography variant="body2" sx={styles.formLabel}>{locationFieldLabel} *</Typography>
            <FolderTreeSelect
              activeNodeId={targetFolderId}
              data={existing}
              dataTest="folder-accordion-select"
              disabled={isPublicationCopy ? !publicationFolderStructure || !publicationLocation : !folderStructure}
              maxMenuHeight={FOLDER_SELECT_MENU_HEIGHT}
              onNodeSelect={(node) => setTargetFolderId(node.id)}
              showFilter={true}
              filterPlaceholder={isPublicationCopy ? publicationViewMessages.filterSections : messages.filterFolders}
            />
            {location !== ECopyLocations.NO_FOLDER && nestedTooDeep && (
              <FormHelperText error sx={styles.errorMsg}>
                {isFromPublication ? publicationViewMessages.nestedTooDeep : messages.nestedTooDeep}
              </FormHelperText>
            )}
          </>
        )}
        {showNameField && (
          <>
            <Box sx={styles.spacer} />
            <Box sx={styles.labelRow}>
              <Typography id="copy-folder-name-label" variant="body2" sx={styles.formLabel}>
                {isFromPublication ? publicationViewMessages.sectionName : messages.folderName} *
              </Typography>
              <Typography variant="caption" sx={styles.characterCounter}>
                {trimmedFolderName.length}/60
              </Typography>
            </Box>
            <TextField
              fullWidth
              size="small"
              sx={styles.textField}
              placeholder={isFromPublication ? publicationViewMessages.sectionName : messages.folderName}
              value={folderName}
              onChange={(e) => setFolderName(e.target.value)}
              disabled={isPublicationCopy && !publicationLocation.length}
              inputProps={{ 'data-test': 'folder-name-input', className: 'no-format', 'aria-labelledby': 'copy-folder-name-label' }}
            />
            {location === ECopyLocations.NEW_FOLDER && isPublicationCopy && nestedTooDeep && (
              <FormHelperText error sx={styles.errorMsg}>
                {isFromPublication ? publicationViewMessages.nestedTooDeep : messages.nestedTooDeep}
              </FormHelperText>
            )}
          </>
        )}
      </Box>
    );
  };

  return (
    <div data-test="copy-folder-modal">
      <Dialog open={true} onClose={onClose} fullWidth maxWidth="sm" PaperProps={{ sx: styles.dialogPaper }} aria-labelledby="copy-folder-dialog-title">
        <DialogTitle id="copy-folder-dialog-title" sx={styles.dialogTitle}>
          {isPublicationCopy ? publicationViewMessages.copySection : messages.copyFolder}
          <IconButton aria-label="close" onClick={onClose} size="small" sx={styles.closeButton}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={styles.dialogContent}>
          <Typography variant="body2" color="text.secondary">
            {isPublicationCopy ? publicationViewMessages.copySectionDescription : messages.copyFolderDescription}
          </Typography>

          <Box sx={styles.folderNameBlock} data-test="copy-folder-title">
            <Typography variant="body2">
              Copying <strong>{folderToCopy.displayName}</strong>
            </Typography>
          </Box>

          <Box>
            <FormControl component="fieldset">
              <FormLabel component="legend" sx={styles.formLabel}>{messages.copyToFolder} / {messages.copyToPublication}</FormLabel>
              <RadioGroup
                sx={styles.radioGroup}
                value={copyPlan}
                onChange={handleCopyPlanChange}
                data-test="copy-radio-group"
                row
              >
                <FormControlLabel
                  value={COPY_PLAN_TO_FOLDER}
                  sx={styles.radioButton}
                  control={
                    <Radio
                      inputProps={{ 'data-test': 'copy-plan-to-folder', className: 'no-format' } as React.InputHTMLAttributes<HTMLInputElement>}
                    />
                  }
                  label={messages.copyToFolder}
                />
                <FormControlLabel
                  value={COPY_PLAN_TO_PUBLICATION}
                  sx={styles.radioButton}
                  control={
                    <Radio
                      inputProps={{ 'data-test': 'copy-plan-to-publication', className: 'no-format' } as React.InputHTMLAttributes<HTMLInputElement>}
                    />
                  }
                  label={messages.copyToPublication}
                />
              </RadioGroup>
            </FormControl>
          </Box>

          <Box sx={styles.spacer}>
            <Box sx={styles.labelRow}>
              <Typography id="copy-folder-title-label" variant="body2" sx={styles.formLabel}>
                {isPublicationCopy ? messages.newSectionName : messages.newFolderName}
              </Typography>
              <Typography variant="caption" sx={styles.characterCounter}>
                {trimmedFolderTitle.length}/60
              </Typography>
            </Box>
            <TextField
              fullWidth
              size="small"
              sx={styles.textField}
              value={folderTitle}
              onChange={(e) => setFolderTitle(e.target.value)}
              inputProps={{ 'data-test': 'folder-title-input', className: 'no-format', 'aria-labelledby': 'copy-folder-title-label' }}
            />
          </Box>

          {isPublicationCopy && (
            <Box sx={styles.spacer}>
              <Typography variant="body2" sx={styles.formLabel}>Publication Location *</Typography>
              <FolderTreeSelect
                activeNodeId={publicationLocation}
                data={pubFolders?.map((folder) => ({ ...folder, children: [] })) || []}
                dataTest="publication-location-accordion-select"
                disabled={!pubFolders}
                maxMenuHeight={FOLDER_SELECT_MENU_HEIGHT}
                onNodeSelect={(node) => setPublicationLocation(node.id)}
                showFilter={true}
                filterPlaceholder={'Select...'}
              />
            </Box>
          )}

          <Box sx={styles.spacer}>
            <FormControl component="fieldset">
              <FormLabel component="legend" sx={styles.formLabel}>{messages.location}</FormLabel>
              <RadioGroup
                sx={styles.radioGroup}
                value={location}
                onChange={(_e, value) => setLocation(value)}
                data-test="location-radio-group"
              >
                {locationToChoose.map((option) => (
                  <FormControlLabel
                    key={option.value}
                    value={option.value}
                    sx={styles.radioButton}
                    control={
                      <Radio
                        inputProps={{ 'data-test': option.dataTest, name: option.value, className: 'no-format' } as React.InputHTMLAttributes<HTMLInputElement>}
                      />
                    }
                    label={
                      <Box>
                        {isPublicationCopy ? publicationViewMessages[option.labelKey] : messages[option.labelKey]}
                        {location === option.value && renderRadioDescription({
                          description: isPublicationCopy
                            ? publicationViewMessages[option.descriptionKey]
                            : messages[option.descriptionKey],
                          showLocationField: option.displayLocationField,
                          locationFieldLabel: messages[option.locationFieldLabelKey],
                          showNameField: option.displayNameField
                        })}
                      </Box>
                    }
                  />
                ))}
              </RadioGroup>
            </FormControl>
          </Box>
        </DialogContent>

        <DialogActions sx={styles.dialogActions}>
          <Button
            variant="outlined"
            color="secondary"
            size="small"
            onClick={onClose}
            data-test="cancel-copy-folder-button"
          >
            {messages.cancel}
          </Button>
          <Button
            variant="contained"
            color="primary"
            size="small"
            onClick={onCopyFolder}
            data-test="confirm-copy-folder-button"
            disabled={!isFormValid()}
            startIcon={isLoading ? <CircularProgress size={16} color="inherit" /> : undefined}
          >
            {messages.copy}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};
